import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { ApiUrlProvider } from './api-url.provider';
import { TimesheetSearchModel } from '../shared/models/timesheet-search-model';
import { IApiResult } from '../shared/models/api-result';
import { Observable } from 'rxjs';
import { Timesheet } from '../shared/models/timesheet';
import { format } from 'date-fns';
import { TimesheetEntity } from '../shared/models/timesheet-entity';
import { PpsUtilityService } from './pps-utility.service';
import { IRosterCode } from '../shared/models/roster-code';
import { TimesheetGenerationModel } from '../shared/models/timesheet-generation-model';
import { IEmployeePayrollRosterModel } from '../shared/models/employee-payroll-roster-model';

@Injectable({
  providedIn: 'root'
})
export class TimesheetService {
  constructor(
    private http: HttpClient,
    private util: PpsUtilityService,
    private apiUrlProvider: ApiUrlProvider) { }

  getTimesheets(searchModel: TimesheetSearchModel): Observable<IApiResult<Timesheet[]>>{
    const params = new HttpParams()
      .set('from', format(searchModel.fromDate, "yyyy-MM-dd"))
      .set('to', format(searchModel.toDate, "yyyy-MM-dd"))
      .set('groupId', searchModel.groupId.toString())
      .set('employeeId', searchModel.employeeId.toString())
      .set('jobCodeId', searchModel.jobCodeId.toString())
      .set('employerId', searchModel.employerId);
    return this.http.get<IApiResult<Timesheet[]>>(`${this.apiUrlProvider.url}/timesheet`, {params});
  }

  getProjectTimesheets(jobCodeId: number): Observable<IApiResult<Timesheet[]>>{
    return this.http.get<IApiResult<Timesheet[]>>(`${this.apiUrlProvider.url}/timesheet/project/${jobCodeId}`);
  }
  
  getTimesheetEntities(): Observable<IApiResult<TimesheetEntity>>{
    return this.http.get<IApiResult<TimesheetEntity>>(`${this.apiUrlProvider.url}/timesheet/entities`);
  }    
  
  addTimesheet(timesheet: Timesheet): Observable<IApiResult<Timesheet>>
  {
    return this.http.post<IApiResult<Timesheet>>(`${this.apiUrlProvider.url}/timesheet`, timesheet);
  }
  
  updateTimesheet(timesheet: Timesheet): Observable<IApiResult<Timesheet>>
  {
    return this.http.post<IApiResult<Timesheet>>(`${this.apiUrlProvider.url}/timesheet/${timesheet.id}/update`, timesheet);
  }
  
  deleteTimesheet(timesheetId: number): Observable<IApiResult<string>>{
    return this.http.post<IApiResult<string>>(`${this.apiUrlProvider.url}/timesheet/${timesheetId}/delete`, null);
  }
  
  updateTimesheetWithPut(timesheet: Timesheet): Observable<IApiResult<Timesheet>>
  {
    return this.http.put<IApiResult<Timesheet>>(`${this.apiUrlProvider.url}/timesheet`, timesheet);
  }
  
  generateTimesheet(timesheetGenerationModel: TimesheetGenerationModel): Observable<IApiResult<number>>
  {
    var body: any = {
      employeeIds: timesheetGenerationModel.employees.map(e => e.id).join(","),
      from: timesheetGenerationModel.from,
      to: timesheetGenerationModel.to,
      jobCodeId: timesheetGenerationModel.jobCode.id,
      hours: timesheetGenerationModel.hours,
      dsHours: timesheetGenerationModel.dsHours,
      nsHours: timesheetGenerationModel.nsHours
    };

    return this.http.post<IApiResult<number>>(`${this.apiUrlProvider.url}/timesheet/generate`, body);
  }
  
  generateTimesheetPreview(timesheetGenerationModel: TimesheetGenerationModel): Observable<IApiResult<IEmployeePayrollRosterModel[]>>
  {
    var body: any = {
      employeeIds: timesheetGenerationModel.employees.map(e => e.id).join(","),
      from: timesheetGenerationModel.from,
      to: timesheetGenerationModel.to
    };

    return this.http.post<IApiResult<IEmployeePayrollRosterModel[]>>(`${this.apiUrlProvider.url}/timesheet/generate/preview`, body);
  }

  deleteTimesheetWithDelete(timesheetId: number): Observable<IApiResult<string>>{
    return this.http.delete<IApiResult<string>>(`${this.apiUrlProvider.url}/timesheet/${timesheetId}`);
  }
  
  downloadTimesheets(searchModel: TimesheetSearchModel, validationReport: boolean, whenDone){
    let url = `${this.apiUrlProvider.url}/timesheet/download`;
    const params = new HttpParams()
      .set('from', format(searchModel.fromDate, "yyyy-MM-dd"))
      .set('to', format(searchModel.toDate, "yyyy-MM-dd"))
      .set('groupId', searchModel.groupId.toString())
      .set('employeeId', searchModel.employeeId.toString())
      .set('jobCodeId', searchModel.jobCodeId.toString())
      .set('employerId', searchModel.employerId)
      .set('validationReport', validationReport);

    return this.http
      .get(url, {params, responseType: 'blob'})
      .subscribe(res => {
        let objectUrl = window.URL.createObjectURL(res);
        let imageAnchor: HTMLAnchorElement = document.createElement("a");
        imageAnchor.href = objectUrl;
        imageAnchor.target = "_blank";
        imageAnchor.download = `${validationReport ? "invalid_" : ""}timesheets_${format(searchModel.fromDate, "yyyyMMdd")}_${format(searchModel.toDate, "yyyyMMdd")}.xlsx`;
        imageAnchor.click();
        window.URL.revokeObjectURL(objectUrl);
      }, _ => {}, () => { 
        if (whenDone){
          whenDone();
        }
      });
  }

  /**
   * 
   * @param timesheet 
   * @param newTotalHours 
   * @param single 
   * @returns {Message, type} 
   *  type = 0, no warning message
   *  type = 1, no sub warning message needed
   *  type = 2, no sub warning message needed
   *  type = 3, has sub warning
   */
  getWarningMessage(timesheet: Timesheet,  newTotalHours: number, single: boolean){
    let rosterCode:IRosterCode = timesheet.shift;
    let payrollRosterCode = timesheet.payrollRosterCode;
    var result = {
      message: "",
      type: 0
    };
    /*
    if (rosterCode.code !== payrollRosterCode){
      result = {
        message: single ? `
          <span class="text-bold">${this.util.getEmployeeName(timesheet.employee)}</span>: 
          Work shift doesn't match roster code 
          <span class="text-danger text-bold">[
            ${rosterCode.code} : ${payrollRosterCode ? payrollRosterCode: "NOT SET"}
          ]</span><br><br>
          Click <span class="text-danger text-bold">[ Yes ]</span> to continue to enter hours, 
                  <span class="text-primary text-bold">[ No ]</span> to cancel.`:
          `<span class="text-bold">${this.util.getEmployeeName(timesheet.employee)}</span>: 
          Work shift doesn't match roster code 
          <span class="text-danger text-bold">[${rosterCode.code} : ${payrollRosterCode ? payrollRosterCode: "NOT SET"}]</span>`,
        type: 1
      };
      return result;
    }

    if (payrollRosterCode === "DAY" && newTotalHours < 11.5)
    {
      result = {
        message:  single ? 
                  `${this.util.getEmployeeName(timesheet.employee)} will be entered less than 
                  <span class='text-danger text-bold'>11.5 hours</span>
                  (total <span class='text-danger text-bold'>${newTotalHours} hours</span>) after this change.<br><br>
                  Click <span class="text-danger text-bold">[ Yes ]</span> to continue to enter hours, 
                  <span class="text-primary text-bold">[ No ]</span> to cancel.`:
                  `The following employees will be entered less than 
                  <span class='text-danger text-bold'>11.5 hours</span>
                  after this action.`,
        type: single ? 2 : 3
      }
      return result;
    }

    if (payrollRosterCode === "NIGHT" && newTotalHours < 12)
    {
      result = {
        message:  single ? 
                  `${this.util.getEmployeeName(timesheet.employee)} will be entered less than 
                  <span class='text-danger text-bold'>12 hours</span>
                  (total <span class='text-danger text-bold'>${newTotalHours} hours</span>) after this change.<br><br>
                  Click <span class="text-danger text-bold">[ Yes ]</span> to continue to enter hours, 
                  <span class="text-primary text-bold">[ No ]</span> to cancel.`:
                  `The following employees will be entered less than 
                  <span class='text-danger text-bold'>12 hours</span>
                  after this action.`,
        type: single ? 2 : 3
      };
      return result;
    }

    if ((payrollRosterCode === "AL" ||
      payrollRosterCode === "PL" ||
      payrollRosterCode === "CL" ||
      payrollRosterCode === "LWOP" ||
      payrollRosterCode === "PO" ||
      payrollRosterCode === "RDO") &&
      newTotalHours < 7.6)
    {
      result = {
        message: single ? 
                `${this.util.getEmployeeName(timesheet.employee)} will be entered less than 
                <span class='text-danger text-bold'>7.6 hours</span>
                (total <span class='text-danger text-bold'>${newTotalHours} hours</span>) after this change.<br><br>
                Click <span class="text-danger text-bold">[ Yes ]</span> to continue to enter hours, 
                <span class="text-primary text-bold">[ No ]</span> to cancel.`:
                `The following employees will be entered less than 
                <span class='text-danger text-bold'>7.6 hours</span> 
                after this action.`,
        type: single ? 2 : 3
      };
      return result;
    }
    */
    if (newTotalHours > 12){
      result = {
        message: single ?
                `${this.util.getEmployeeName(timesheet.employee)} will be entered more than 
                <span class='text-danger text-bold'>12 hours</span>
                (total <span class='text-danger text-bold'>${newTotalHours} hours</span>) after this change.<br><br>
                Click <span class="text-danger text-bold">[ Yes ]</span> to continue to enter hours, 
                <span class="text-primary text-bold">[ No ]</span> to cancel.`:
                `The following employees will be entered more than 
                <span class='text-danger text-bold'>12 hours</span> after this action.`,
        type: single ? 2 : 3
      }
      return result;
    }

    return result;
  } 
}